import React, { useState, useCallback, useRef, useEffect } from 'react';
import { getCheckoutOptions, formatCheckout } from './utils/checkouts';
import './styles/index.css';
import './styles/jesus-salate.css';
import SponsorsStrip from './SponsorsStrip';
import logo180 from '../../assets/logo180.png';

// Galería de imágenes del desafío
const GALLERY_IMAGES = [
  {
    id: 1,
    src: '/images/JesusSalate/fondoJesus.png',
    title: 'Clasificación Final Chile 2025',
    description: 'Victoria que lo llevó al Mundial PDC'
  },
  {
    id: 2,
    src: '/images/JesusSalate/EntrenoJesus.jpg',
    title: 'Cuadro de entrenamiento',
    description: '100 días de constancia y sacrificio'
  },
  {
    id: 3,
    src: '/images/JesusSalate/JesusApp2.jpg',
    title: 'Con la App 501 Dart League',
    description: 'La herramienta detrás del éxito'
  }
];

// Desafíos del entrenamiento Jesús Salate
const CHALLENGES_NORMAL = [
  { id: 1, target: 101, maxThrows: 2, maxDarts: 6, description: '101 en 2 rondas', type: 'normal' },
  { id: 2, target: 201, maxThrows: 3, maxDarts: 9, description: '201 en 3 rondas', type: 'normal' },
  { id: 3, target: 301, maxThrows: 4, maxDarts: 12, description: '301 en 4 rondas', type: 'normal' },
  { id: 4, target: 401, maxThrows: 5, maxDarts: 15, description: '401 en 5 rondas', type: 'normal' },
  { id: 5, target: 501, maxThrows: 6, maxDarts: 18, description: '501 en 6 rondas', type: 'normal' },
  { id: 6, target: 0, maxThrows: 0, maxDarts: 0, description: 'Rueda de Dobles', type: 'doubles-wheel' },
  { id: 7, target: 501, maxThrows: 6, maxDarts: 18, description: '501 en 6 rondas (mejor de 5)', requireCompletions: 3, type: 'normal' },
];

const CHALLENGES_EASY = [
  { id: 1, target: 101, maxThrows: 3, maxDarts: 9, description: '101 en 3 rondas', type: 'normal' },
  { id: 2, target: 201, maxThrows: 4, maxDarts: 12, description: '201 en 4 rondas', type: 'normal' },
  { id: 3, target: 301, maxThrows: 5, maxDarts: 15, description: '301 en 5 rondas', type: 'normal' },
  { id: 4, target: 401, maxThrows: 6, maxDarts: 18, description: '401 en 6 rondas', type: 'normal' },
  { id: 5, target: 501, maxThrows: 7, maxDarts: 21, description: '501 en 7 rondas', type: 'normal' },
  { id: 6, target: 0, maxThrows: 0, maxDarts: 0, description: 'Rueda de Dobles', type: 'doubles-wheel' },
  { id: 7, target: 501, maxThrows: 7, maxDarts: 21, description: '501 en 7 rondas (mejor de 5)', requireCompletions: 3, type: 'normal' },
];

// Orden de los números en la diana
const DARTBOARD_ORDER = [20, 1, 18, 4, 13, 6, 10, 15, 2, 17, 3, 19, 7, 16, 8, 11, 14, 9, 12, 5];

// Desafío BONUS
const BONUS_CHALLENGE = { id: 8, target: 501, maxThrows: 4, maxDarts: 12, description: '501 en 4 rondas', type: 'normal' };

const MOTIVATIONAL_MESSAGES = [
  '¡Muy bien! Has pasado el primer nivel. ¡Sigue así!',
  '¡Excelente! Segundo nivel completado. ¡Vas por buen camino!',
  '¡Impresionante! Ya llevas 3 desafíos. ¡No pares ahora!',
  '¡Increíble! Cuarto nivel superado. ¡Casi lo tienes!',
  '🏆 ¡FENOMENAL! Quinto nivel completado. ¡Un desafío más!',
];

const JesusSalateTraining = ({ onBack }) => {
  // Selección de modo
  const [mode, setMode] = useState(null); // null = pantalla de selección, 'normal', 'easy'
  const [showExitConfirm, setShowExitConfirm] = useState(false);
  
  // Carrusel de fotos
  const [activeSlide, setActiveSlide] = useState(0);
  const carouselRef = useRef(null);
  const touchStartX = useRef(0);
  const touchEndX = useRef(0);
  const roundsPanelRef = useRef(null);

  const scrollToSlide = (index) => {
    if (carouselRef.current) {
      const slideWidth = carouselRef.current.querySelector('.js-gallery-slide')?.offsetWidth || 280;
      carouselRef.current.scrollTo({ left: index * (slideWidth + 16), behavior: 'smooth' });
      setActiveSlide(index);
    }
  };

  const handleTouchStart = (e) => { touchStartX.current = e.touches[0].clientX; };
  const handleTouchEnd = (e) => {
    touchEndX.current = e.changedTouches[0].clientX;
    const diff = touchStartX.current - touchEndX.current;
    if (Math.abs(diff) > 50) {
      if (diff > 0 && activeSlide < GALLERY_IMAGES.length - 1) scrollToSlide(activeSlide + 1);
      else if (diff < 0 && activeSlide > 0) scrollToSlide(activeSlide - 1);
    }
  };

  // Auto-scroll del carrusel: cambia de foto cada 4 segundos
  useEffect(() => {
    if (mode !== null) return; // Solo en la pantalla intro
    const timer = setInterval(() => {
      setActiveSlide(prev => {
        const next = (prev + 1) % GALLERY_IMAGES.length;
        if (carouselRef.current) {
          const slideWidth = carouselRef.current.querySelector('.js-gallery-slide')?.offsetWidth || 280;
          carouselRef.current.scrollTo({ left: next * (slideWidth + 16), behavior: 'smooth' });
        }
        return next;
      });
    }, 4000);
    return () => clearInterval(timer);
  }, [mode]);
  
  const CHALLENGES = mode === 'easy' ? CHALLENGES_EASY : CHALLENGES_NORMAL;
  const isEasyMode = mode === 'easy';

  // Estado del juego
  const [currentChallengeIndex, setCurrentChallengeIndex] = useState(0);
  const [score, setScore] = useState(101);
  const [input, setInput] = useState('');
  const [dartsThrown, setDartsThrown] = useState(0);
  const [throwsUsed, setThrowsUsed] = useState(0);
  const [throwHistory, setThrowHistory] = useState([]);
  const [failedAttempts, setFailedAttempts] = useState(0);

  // Auto-scroll del panel de rondas al último elemento
  useEffect(() => {
    if (roundsPanelRef.current && throwHistory.length > 0) {
      requestAnimationFrame(() => {
        const panel = roundsPanelRef.current;
        if (!panel) return;
        const isMobile = window.matchMedia('(max-width: 768px)').matches;
        if (isMobile) {
          panel.scrollLeft = panel.scrollWidth;
        } else {
          panel.scrollTop = panel.scrollHeight;
        }
      });
    }
  }, [throwHistory.length]);
  const [moveHistory, setMoveHistory] = useState([]);
  
  // Nivel 7: mejor de 5
  const [level7Completions, setLevel7Completions] = useState(0);
  const [level7RivalScore, setLevel7RivalScore] = useState(0);
  const [level7Averages, setLevel7Averages] = useState([]);
  
  // Intentos por nivel
  const [attemptsPerLevel, setAttemptsPerLevel] = useState([0, 0, 0, 0, 0, 0, 0]);
  
  // Rueda de dobles
  const [doublesHit, setDoublesHit] = useState(new Set());
  const [doublesWheelRounds, setDoublesWheelRounds] = useState(0);
  const [doublesWheelFinalRounds, setDoublesWheelFinalRounds] = useState(0);
  
  // Modal de dardos de cierre
  const [showDartsModal, setShowDartsModal] = useState(false);
  const [pendingWinData, setPendingWinData] = useState(null);
  
  // Alert system
  const [alert, setAlert] = useState({ visible: false, title: '', message: '', buttons: [], type: 'info' });
  
  // 180 banner
  const [show180, setShow180] = useState(false);
  
  // Resultados finales
  const [showResults, setShowResults] = useState(false);

  // Bonus
  const [bonusActive, setBonusActive] = useState(false);
  const [showBonusConfirm, setShowBonusConfirm] = useState(false);
  const [bonusAttempts, setBonusAttempts] = useState(0);
  const [bonusCompleted, setBonusCompleted] = useState(false);
  const [bonusBestAverage, setBonusBestAverage] = useState(null);
  const [bonusClosingDarts, setBonusClosingDarts] = useState(null);

  const currentChallenge = bonusActive ? BONUS_CHALLENGE : (CHALLENGES[currentChallengeIndex] || CHALLENGES[0]);
  const currentAverage = throwsUsed > 0 
    ? Math.round(((currentChallenge.target - score) / (throwsUsed * 3)) * 3 * 100) / 100 
    : 0;

  // ========== HANDLERS ==========

  const showAlert = useCallback((title, message, buttons, type = 'info') => {
    setAlert({ visible: true, title, message, buttons, type });
  }, []);

  const closeAlert = useCallback(() => {
    setAlert(prev => ({ ...prev, visible: false }));
  }, []);

  const handleNumberPress = (num) => {
    if (input.length < 3) {
      const newInput = input + num;
      const value = parseInt(newInput);
      if (value <= 180) setInput(newInput);
    }
  };

  const handleBackspace = () => setInput(input.slice(0, -1));

  const handleBust = () => {
    const newThrowsUsed = throwsUsed + 1;
    setThrowsUsed(newThrowsUsed);
    setThrowHistory(prev => [...prev, 'BUST']);
    setMoveHistory(prev => [...prev, { score, throwsUsed, dartsThrown, points: 0, isBust: true }]);
    setInput('');

    if (newThrowsUsed >= currentChallenge.maxThrows) {
      setFailedAttempts(prev => prev + 1);
      
      if (bonusActive) {
        setBonusAttempts(prev => prev + 1);
        showAlert('❌ No lo conseguiste', `No completaste 501 en 4 rondas.\nBUST en la ronda ${newThrowsUsed}.`,
          [{ text: 'Reintentar', onClick: () => { closeAlert(); resetCurrentChallenge(); }, style: 'primary' }], 'warning');
      } else if (currentChallengeIndex === 6) {
        const newRivalScore = level7RivalScore + 1;
        setLevel7RivalScore(newRivalScore);
        
        if (newRivalScore >= 3) {
          showAlert('💔 Perdiste', `MARCADOR FINAL\nTÚ ${level7Completions} - 3 RIVAL\n\nEl rival llegó a 3 puntos primero.\nEl nivel 7 se reiniciará.`, 
            [{ text: 'Reiniciar', onClick: () => { closeAlert(); resetLevel7(); }, style: 'primary' }], 'warning');
        } else {
          showAlert('❌ Punto para el rival', `MARCADOR\nTÚ ${level7Completions} - ${newRivalScore} RIVAL\n\nBUST. El rival está a ${3 - newRivalScore} puntos de ganar.`,
            [{ text: 'Continuar', onClick: () => { closeAlert(); resetCurrentChallenge(); }, style: 'primary' }], 'warning');
        }
      } else {
        const newAttempts = [...attemptsPerLevel];
        newAttempts[currentChallengeIndex]++;
        setAttemptsPerLevel(newAttempts);
        showAlert('❌ Fallaste', `No completaste en ${currentChallenge.maxThrows} rondas.`,
          [{ text: 'Reintentar', onClick: () => { closeAlert(); resetCurrentChallenge(); }, style: 'primary' }], 'warning');
      }
    }
  };

  const undoLastMove = () => {
    if (moveHistory.length > 0) {
      const lastMove = moveHistory[moveHistory.length - 1];
      setScore(lastMove.score);
      setThrowsUsed(lastMove.throwsUsed);
      setDartsThrown(lastMove.dartsThrown);
      setInput('');
      setMoveHistory(prev => prev.slice(0, -1));
      setThrowHistory(prev => prev.slice(0, -1));
    }
  };

  const handleSubmit = () => {
    if (input === '') return;
    const points = parseInt(input);
    
    if (points < 0 || points > 180) { setInput(''); return; }
    if (points > score) {
      showAlert('⚠️ Inválido', 'No puedes introducir más puntos de los que te quedan.',
        [{ text: 'OK', onClick: closeAlert, style: 'primary' }]);
      setInput('');
      return;
    }

    const newScore = score - points;
    if (newScore === 1) {
      showAlert('⚠️ Inválido', 'No puedes quedarte con 1 punto.',
        [{ text: 'OK', onClick: closeAlert, style: 'primary' }]);
      setInput('');
      return;
    }

    const newThrowsUsed = throwsUsed + 1;
    const newDartsThrown = dartsThrown + 3;
    const newHistory = [...throwHistory, points];

    setMoveHistory(prev => [...prev, { score, throwsUsed, dartsThrown, points }]);

    if (points === 180) {
      setShow180(true);
      setTimeout(() => setShow180(false), 2200);
    }

    setThrowHistory(newHistory);
    setDartsThrown(newDartsThrown);
    setThrowsUsed(newThrowsUsed);

    // Victoria
    if (newScore === 0) {
      setScore(0);
      if (bonusActive) {
        // Bonus: preguntar dardos de cierre
        if (points > 110) {
          processWinWithDarts(3, { newThrowsUsed, points });
        } else {
          setPendingWinData({ newThrowsUsed, points });
          setShowDartsModal(true);
        }
      } else if (currentChallengeIndex >= 4) {
        // Niveles 5, 6, 7: preguntar dardos de cierre
        if (points > 110) {
          processWinWithDarts(3, { newThrowsUsed, points });
        } else {
          setPendingWinData({ newThrowsUsed, points });
          setShowDartsModal(true);
        }
      } else {
        // Niveles 1-4: pasar directamente
        showAlert('🎯 ¡Completado!', `¡Has superado el Desafío ${currentChallengeIndex + 1}!\n${currentChallenge.description}\nRondas usadas: ${newThrowsUsed}/${currentChallenge.maxThrows}`,
          [{ text: 'Siguiente Nivel', onClick: () => { closeAlert(); nextChallenge(); }, style: 'success' }], 'success');
      }
      return;
    }

    // Fallo por tiradas agotadas
    if (newThrowsUsed >= currentChallenge.maxThrows) {
      setFailedAttempts(prev => prev + 1);
      if (bonusActive) {
        setBonusAttempts(prev => prev + 1);
        showAlert('❌ No lo conseguiste', `No completaste 501 en 4 rondas.\nTe quedaron ${newScore} puntos.`,
          [{ text: 'Reintentar', onClick: () => { closeAlert(); resetCurrentChallenge(); }, style: 'primary' }], 'warning');
      } else if (currentChallengeIndex === 6) {
        const newRivalScore = level7RivalScore + 1;
        setLevel7RivalScore(newRivalScore);
        if (newRivalScore >= 3) {
          showAlert('💔 Perdiste', `MARCADOR FINAL\nTÚ ${level7Completions} - 3 RIVAL`,
            [{ text: 'Reiniciar', onClick: () => { closeAlert(); resetLevel7(); }, style: 'primary' }], 'warning');
        } else {
          showAlert('❌ Punto rival', `MARCADOR\nTÚ ${level7Completions} - ${newRivalScore} RIVAL`,
            [{ text: 'Continuar', onClick: () => { closeAlert(); resetCurrentChallenge(); }, style: 'primary' }], 'warning');
        }
      } else {
        const newAttempts = [...attemptsPerLevel];
        newAttempts[currentChallengeIndex]++;
        setAttemptsPerLevel(newAttempts);
        showAlert('❌ Fallaste', `No completaste en ${currentChallenge.maxThrows} rondas.`,
          [{ text: 'Reintentar', onClick: () => { closeAlert(); resetCurrentChallenge(); }, style: 'primary' }], 'warning');
      }
      return;
    }

    setScore(newScore);
    setInput('');
  };

  const processWinWithDarts = (closingDarts, winData) => {
    const { newThrowsUsed } = winData;
    const totalDarts = ((newThrowsUsed - 1) * 3) + closingDarts;
    const average = Math.round((currentChallenge.target / totalDarts) * 3 * 100) / 100;

    // Bonus
    if (bonusActive) {
      setBonusCompleted(true);
      setBonusBestAverage(average);
      setBonusClosingDarts(closingDarts);
      showAlert('🌟 ¡BONUS COMPLETADO!', `¡Increíble! Has hecho 501 en 4 rondas.\n\nAverage: ${average}\nDardos: ${totalDarts}\n(Cerrado con ${closingDarts} dardo${closingDarts > 1 ? 's' : ''})\nIntentos: ${bonusAttempts + 1}`,
        [{ text: 'Ver Resultados', onClick: () => { closeAlert(); setBonusActive(false); setShowResults(true); }, style: 'success' }], 'success');
      setPendingWinData(null);
      return;
    }

    // Nivel 7 (mejor de 5)
    if (currentChallengeIndex === 6) {
      const newCompletions = level7Completions + 1;
      setLevel7Completions(newCompletions);
      setLevel7Averages(prev => [...prev, average]);

      if (newCompletions < 3) {
        showAlert('🎯 ¡Punto para ti!', `MARCADOR\nTÚ ${newCompletions} - ${level7RivalScore} RIVAL\n\nAverage: ${average}\n(Cerrado con ${closingDarts} dardo${closingDarts > 1 ? 's' : ''})`,
          [{ text: 'Continuar', onClick: () => { closeAlert(); resetCurrentChallenge(); }, style: 'success' }], 'success');
      } else {
        // ¡Ganó!
        showAlert('🏆 ¡GANASTE!', `MARCADOR FINAL\nTÚ 3 - ${level7RivalScore} RIVAL\n\nAverage final: ${average}\n¡Eres un verdadero maestro!`,
          [{ text: 'Ver Resultados', onClick: () => { closeAlert(); setShowResults(true); }, style: 'success' }], 'success');
      }
      setPendingWinData(null);
      return;
    }

    // Nivel 5
    const msg = MOTIVATIONAL_MESSAGES[currentChallengeIndex] || '¡Excelente!';
    showAlert('🎯 ¡Completado!', `${msg}\n\nAverage: ${average}\n(Cerrado con ${closingDarts} dardo${closingDarts > 1 ? 's' : ''})`,
      [{ text: 'Siguiente', onClick: () => { closeAlert(); nextChallenge(); }, style: 'success' }], 'success');
    setPendingWinData(null);
  };

  // ========== RESETS ==========

  const resetCurrentChallenge = () => {
    setScore(currentChallenge.target);
    setInput('');
    setDartsThrown(0);
    setThrowsUsed(0);
    setThrowHistory([]);
    setMoveHistory([]);
  };

  const resetLevel7 = () => {
    setLevel7Completions(0);
    setLevel7RivalScore(0);
    setLevel7Averages([]);
    resetCurrentChallenge();
  };

  const nextChallenge = () => {
    if (currentChallengeIndex < CHALLENGES.length - 1) {
      const newAttempts = [...attemptsPerLevel];
      newAttempts[currentChallengeIndex]++;
      setAttemptsPerLevel(newAttempts);
      
      const nextIndex = currentChallengeIndex + 1;
      setCurrentChallengeIndex(nextIndex);
      setScore(CHALLENGES[nextIndex].target);
      setInput('');
      setDartsThrown(0);
      setThrowsUsed(0);
      setThrowHistory([]);
      setMoveHistory([]);
    }
  };

  const resetTraining = () => {
    setCurrentChallengeIndex(0);
    setScore(CHALLENGES[0].target);
    setInput('');
    setDartsThrown(0);
    setThrowsUsed(0);
    setThrowHistory([]);
    setFailedAttempts(0);
    setMoveHistory([]);
    setLevel7Completions(0);
    setLevel7RivalScore(0);
    setLevel7Averages([]);
    setAttemptsPerLevel([0, 0, 0, 0, 0, 0, 0]);
    setDoublesHit(new Set());
    setDoublesWheelRounds(0);
    setDoublesWheelFinalRounds(0);
    setShowResults(false);
    setBonusActive(false);
    setBonusAttempts(0);
    setBonusCompleted(false);
    setBonusBestAverage(null);
    setBonusClosingDarts(null);
    setShowBonusConfirm(false);
  };

  // ========== RUEDA DE DOBLES ==========

  const handleDoubleHit = (num) => {
    const newSet = new Set(doublesHit);
    if (newSet.has(num)) {
      newSet.delete(num);
    } else {
      newSet.add(num);
      if (newSet.size === 21) {
        setDoublesHit(newSet);
        setDoublesWheelFinalRounds(doublesWheelRounds + 1);
        const finalRounds = doublesWheelRounds + 1;
        showAlert('🎯 ¡Rueda Completada!', `¡Enhorabuena! Has conseguido todos los dobles.\n\nRondas: ${finalRounds}\nDardos totales: ${finalRounds * 3}\nMedia dobles/ronda: ${finalRounds > 0 ? (21 / finalRounds).toFixed(2) : '0'}`,
          [{ text: 'Siguiente Nivel', onClick: () => { closeAlert(); nextChallenge(); }, style: 'success' }], 'success');
        return;
      }
    }
    setDoublesHit(newSet);
  };

  // ========== KEY HANDLER ==========
  const handleKeyDown = useCallback((e) => {
    if (alert.visible || showDartsModal || showResults || showBonusConfirm) return;
    
    if (e.key >= '0' && e.key <= '9') {
      handleNumberPress(e.key);
    } else if (e.key === 'Backspace') {
      handleBackspace();
    } else if (e.key === 'Enter') {
      handleSubmit();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [input, score, throwsUsed, alert.visible, showDartsModal, showResults, showBonusConfirm]);

  React.useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  // ========== PANTALLA DE SELECCIÓN DE MODO ==========
  if (mode === null) {
    return (
      <div className="js-training">
        <div className="js-intro">
          <button className="js-back-btn" onClick={onBack}>← Volver</button>
          <div className="js-intro-header">
            <img src="/images/JesusSalate/logoJesus.png" alt="Jesús Salate" className="js-intro-icon" />
            <h1>Desafío Jesús Salate</h1>
            <p className="js-intro-badge">🇦🇷 Clasificado Mundial PDC Individual • Argentina</p>
          </div>

          {/* Carrusel de fotos */}
          <div className="js-gallery">
            <div 
              className="js-gallery-track" 
              ref={carouselRef}
              onTouchStart={handleTouchStart}
              onTouchEnd={handleTouchEnd}
            >
              {GALLERY_IMAGES.map((img, index) => (
                <div key={img.id} className={`js-gallery-slide ${activeSlide === index ? 'active' : ''}`}>
                  <div className="js-gallery-img-wrapper">
                    <img src={img.src} alt={img.title} className="js-gallery-img" />
                  </div>
                  <h4 className="js-gallery-title">{img.title}</h4>
                  <p className="js-gallery-desc">{img.description}</p>
                </div>
              ))}
            </div>
            <div className="js-gallery-dots">
              {GALLERY_IMAGES.map((_, index) => (
                <button
                  key={index}
                  className={`js-gallery-dot ${activeSlide === index ? 'active' : ''}`}
                  onClick={() => scrollToSlide(index)}
                  aria-label={`Imagen ${index + 1}`}
                />
              ))}
            </div>
            <div className="js-gallery-arrows">
              <button 
                className="js-gallery-arrow" 
                onClick={() => scrollToSlide(Math.max(0, activeSlide - 1))}
                disabled={activeSlide === 0}
              >‹</button>
              <button 
                className="js-gallery-arrow" 
                onClick={() => scrollToSlide(Math.min(GALLERY_IMAGES.length - 1, activeSlide + 1))}
                disabled={activeSlide === GALLERY_IMAGES.length - 1}
              >›</button>
            </div>
          </div>

          <div className="js-intro-description">
            <h3>¿En qué consiste?</h3>
            <p>
              Este es el entrenamiento que Jesús Salate utilizó durante 100 días para prepararse para 6 eventos clasificatorios de la PDC,
              disputados en Panamá, Bahamas y Chile. Esa preparación le llevó a clasificarse al Mundial PDC Individual representando a Argentina.
            </p>
            <p className="js-intro-highlight">
              Una rutina progresiva de 7 niveles que eleva la exigencia paso a paso: primero construye volumen y control, luego precisión en dobles,
              y termina con un cierre competitivo de nivel profesional.
            </p>
            <div className="js-intro-facts">
              <div className="js-intro-fact">
                <strong>100 días</strong>
                <span>de preparación real</span>
              </div>
              <div className="js-intro-fact">
                <strong>6 clasificatorios</strong>
                <span>previos al Mundial</span>
              </div>
              <div className="js-intro-fact">
                <strong>7 niveles</strong>
                <span>de dificultad progresiva</span>
              </div>
            </div>
          </div>

          <div className="js-levels-preview">
            <h3>Cómo se estructura el desafío</h3>
            <div className="js-levels-list">
              <div className="js-level-item">🎯 Nivel 1: 101 puntos en 2 rondas, 6 dardos</div>
              <div className="js-level-item">🎯 Nivel 2: 201 puntos en 3 rondas, 9 dardos</div>
              <div className="js-level-item">🎯 Nivel 3: 301 puntos en 4 rondas, 12 dardos</div>
              <div className="js-level-item">🎯 Nivel 4: 401 puntos en 5 rondas, 15 dardos</div>
              <div className="js-level-item">🎯 Nivel 5: 501 puntos en 6 rondas, 18 dardos</div>
              <div className="js-level-item">🎯 Nivel 6: Rueda de Dobles, completa todos los dobles y bull</div>
              <div className="js-level-item">🏆 Nivel 7: 501 en 6 rondas, consíguelo 3 veces de 5</div>
            </div>
            <div className="js-levels-rules">
              <h4>Reglas clave</h4>
              <ul>
                <li>Niveles 1-5: puedes repetir hasta superar cada objetivo dentro del número de rondas marcado.</li>
                <li>Nivel 6: cambia el foco a precisión pura con la rueda completa de dobles.</li>
                <li>Nivel 7: no basta con hacerlo una vez; tienes que sostener el nivel competitivo en formato mejor de 5.</li>
              </ul>
            </div>
          </div>

          <div className="js-mode-selection">
            <h3>Selecciona dificultad</h3>
            <p className="js-mode-intro">
              Si quieres vivir la rutina tal como la entrenó Jesús, juega en normal. Si todavía estás construyendo base, usa principiante para mantener la lógica del reto con una ronda extra por nivel.
            </p>
            <button className="js-mode-btn normal" onClick={() => { setMode('normal'); setScore(CHALLENGES_NORMAL[0].target); }}>
              <span className="js-mode-emoji">🔥</span>
              <div>
                <strong>Normal</strong>
                <p>La exigencia original que utilizó Jesús Salate para preparar su clasificación</p>
              </div>
            </button>
            <button className="js-mode-btn easy" onClick={() => { setMode('easy'); setScore(CHALLENGES_EASY[0].target); }}>
              <span className="js-mode-emoji">✨</span>
              <div>
                <strong>Principiante</strong>
                <p>Una ronda extra en cada nivel para practicar la estructura sin bajar la calidad del reto</p>
              </div>
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ========== PANTALLA DE RESULTADOS ==========
  if (showResults) {
    const totalIntentos = attemptsPerLevel.slice(0, 5).reduce((a, b) => a + b, 0);
    const avgOfAverages = level7Averages.length > 0 
      ? (level7Averages.reduce((a, b) => a + b, 0) / level7Averages.length).toFixed(2) : '0.00';
    const bestAvg = level7Averages.length > 0 ? Math.max(...level7Averages).toFixed(2) : '0.00';

    return (
      <div className="js-training">
        <div className="js-results">
          <h1>🏆 ¡ENTRENAMIENTO COMPLETADO!</h1>
          <p className="js-results-subtitle">Has superado los 7 desafíos del entrenamiento Jesús Salate</p>
          
          <div className="js-results-section">
            <h3>📊 Niveles 1-5: Puntuaciones</h3>
            {CHALLENGES.slice(0, 5).map((c, i) => (
              <div key={i} className="js-result-row">
                <span>🎯 Nivel {i + 1} ({c.description})</span>
                <span>Intentos: {attemptsPerLevel[i]}</span>
              </div>
            ))}
            <div className="js-result-total">Total intentos (1-5): {totalIntentos}</div>
          </div>

          <div className="js-results-section">
            <h3>🎯 Nivel 6: Rueda de Dobles</h3>
            <div className="js-result-row">
              <span>Tiradas para completar</span>
              <span>{doublesWheelFinalRounds}</span>
            </div>
            <div className="js-result-row">
              <span>Dardos totales</span>
              <span>{doublesWheelFinalRounds * 3}</span>
            </div>
          </div>

          <div className="js-results-section">
            <h3>🏆 Nivel 7: 501 al mejor de 5</h3>
            <div className="js-result-row">
              <span>Marcador final</span>
              <span>TÚ 3 - {level7RivalScore} RIVAL</span>
            </div>
            <div className="js-result-row">
              <span>Average medio</span>
              <span>{avgOfAverages}</span>
            </div>
            <div className="js-result-row">
              <span>Mejor average</span>
              <span>{bestAvg}</span>
            </div>
          </div>

          {/* Secci\u00f3n Bonus */}
          {bonusCompleted && (
            <div className="js-results-section js-bonus-section">
              <h3>🌟 BONUS: 501 en 4 rondas</h3>
              <div className="js-result-row">
                <span>Estado</span>
                <span className="js-bonus-completed">✅ Completado</span>
              </div>
              <div className="js-result-row">
                <span>Average</span>
                <span>{bonusBestAverage?.toFixed(2) || '—'}</span>
              </div>
              <div className="js-result-row">
                <span>Dardos de cierre</span>
                <span>{bonusClosingDarts || '—'}</span>
              </div>
              <div className="js-result-row">
                <span>Intentos</span>
                <span>{bonusAttempts + 1}</span>
              </div>
            </div>
          )}

          <div className="js-results-actions">
            {!bonusCompleted && (
              <button className="js-btn bonus" onClick={() => setShowBonusConfirm(true)}>
                🌟 Modo Bonus
              </button>
            )}
            <button className="js-btn primary" onClick={() => { resetTraining(); setMode(null); }}>Repetir</button>
            <button className="js-btn secondary" onClick={onBack}>Salir</button>
          </div>
        </div>

        {/* Modal confirmaci\u00f3n Bonus */}
        {showBonusConfirm && (
          <div className="js-modal-overlay">
            <div className="js-modal bonus">
              <h2>🌟 Modo Bonus</h2>
              <p className="js-modal-message">
                ¡Desafío extremo desbloqueado!
                {'\n\n'}
                🎯 Completa 501 en solo 4 rondas (12 dardos).
                {'\n\n'}
                Si no lo consigues, se reiniciará hasta que lo logres.
                {'\n'}
                Solo los mejores jugadores pueden hacerlo. ¿Te atreves?
              </p>
              <div className="js-modal-buttons">
                <button className="js-btn default" onClick={() => setShowBonusConfirm(false)}>Me retiro</button>
                <button className="js-btn bonus" onClick={() => {
                  setShowBonusConfirm(false);
                  setShowResults(false);
                  setBonusActive(true);
                  setBonusAttempts(0);
                  setScore(501);
                  setInput('');
                  setDartsThrown(0);
                  setThrowsUsed(0);
                  setThrowHistory([]);
                  setMoveHistory([]);
                  setFailedAttempts(0);
                }}>¡Acepto el desafío!</button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ========== RUEDA DE DOBLES (Nivel 6) ==========
  if (currentChallenge.type === 'doubles-wheel') {
    const wheelSize = 360;
    const center = wheelSize / 2;
    const outerRadius = wheelSize / 2 - 10;
    const innerRadius = outerRadius - 50;
    const bullRadius = 35;
    const startAngle = -99;
    const segmentAngle = 360 / 20;

    return (
      <div className="js-training">
        <div className="js-game-header">
          <button className="js-back-btn" onClick={() => setShowExitConfirm(true)}>← Salir</button>
          <div className="js-header-logos">
            <img src="/Logo26.png" alt="501 Dart League" className="js-app-logo" />
            <img src="/images/JesusSalate/NuevoLogoSinFondoJesus.png" alt="Jesús Salate" className="js-jesus-logo" />
          </div>
          <div className="js-challenge-info">
            <span className="js-challenge-title">Desafío 6/7: Rueda de Dobles</span>
            <span className="js-challenge-mode">{isEasyMode ? '✨ Principiante' : '🔥 Normal'}</span>
          </div>
        </div>

        <div className="js-doubles-layout">
          <div className="js-doubles-instructions">
            <p>1️⃣ Tira tus 3 dardos a dobles</p>
            <p>2️⃣ Marca en la rueda los dobles acertados</p>
            <p>3️⃣ Pulsa "Siguiente Tirada" y repite</p>
          </div>

          <div className="js-doubles-stats">
            <div className="js-doubles-stat">
              <span className="js-doubles-stat-label">Dobles</span>
              <span className="js-doubles-stat-value cyan">{doublesHit.size}/21</span>
            </div>
            <div className="js-doubles-stat">
              <span className="js-doubles-stat-label">Tiradas</span>
              <span className="js-doubles-stat-value green">{doublesWheelRounds}</span>
            </div>
          </div>

          <div className="js-wheel-container">
            <svg viewBox={`0 0 ${wheelSize} ${wheelSize}`}>
              <circle cx={center} cy={center} r={outerRadius} fill="#001a1e" stroke="#333" strokeWidth={2} />
              {DARTBOARD_ORDER.map((num, index) => {
                const a1 = startAngle + index * segmentAngle;
                const a2 = startAngle + (index + 1) * segmentAngle;
                const isHit = doublesHit.has(num);
                const rad1 = (a1 * Math.PI) / 180;
                const rad2 = (a2 * Math.PI) / 180;
                
                const x1o = center + outerRadius * Math.cos(rad1);
                const y1o = center + outerRadius * Math.sin(rad1);
                const x2o = center + outerRadius * Math.cos(rad2);
                const y2o = center + outerRadius * Math.sin(rad2);
                const x1i = center + innerRadius * Math.cos(rad1);
                const y1i = center + innerRadius * Math.sin(rad1);
                const x2i = center + innerRadius * Math.cos(rad2);
                const y2i = center + innerRadius * Math.sin(rad2);
                
                const d = `M ${x1i} ${y1i} L ${x1o} ${y1o} A ${outerRadius} ${outerRadius} 0 0 1 ${x2o} ${y2o} L ${x2i} ${y2i} A ${innerRadius} ${innerRadius} 0 0 0 ${x1i} ${y1i} Z`;
                
                const midAngle = (a1 + a2) / 2;
                const midRad = (midAngle * Math.PI) / 180;
                const textR = (outerRadius + innerRadius) / 2;
                const textX = center + textR * Math.cos(midRad);
                const textY = center + textR * Math.sin(midRad);
                
                const baseColor = index % 2 === 0 ? '#c41e3a' : '#228b22';
                
                return (
                  <g key={num} onClick={() => handleDoubleHit(num)} style={{ cursor: 'pointer' }}>
                    <path d={d} fill={isHit ? '#00C6CF' : baseColor} stroke={isHit ? '#00E5FF' : '#111'} strokeWidth={isHit ? 3 : 1} />
                    <text x={textX} y={textY + 5} fill={isHit ? '#000' : '#fff'} fontSize="14" fontWeight="bold" textAnchor="middle">{num}</text>
                  </g>
                );
              })}
              <circle cx={center} cy={center} r={innerRadius - 5} fill="#001a1e" stroke="#333" strokeWidth={1} />
              <circle cx={center} cy={center} r={bullRadius} fill={doublesHit.has(25) ? '#00C6CF' : '#228b22'} stroke={doublesHit.has(25) ? '#00E5FF' : '#111'} strokeWidth={2} onClick={() => handleDoubleHit(25)} style={{ cursor: 'pointer' }} />
              <circle cx={center} cy={center} r={18} fill={doublesHit.has(25) ? '#00A8B5' : '#c41e3a'} stroke="#111" strokeWidth={1} onClick={() => handleDoubleHit(25)} style={{ cursor: 'pointer' }} />
              <text x={center} y={center + 5} fill={doublesHit.has(25) ? '#000' : '#fff'} fontSize="12" fontWeight="bold" textAnchor="middle" onClick={() => handleDoubleHit(25)} style={{ cursor: 'pointer' }}>BULL</text>
            </svg>
          </div>

          <button className="js-btn primary large" onClick={() => setDoublesWheelRounds(prev => prev + 1)}>
            ➡️ Siguiente Tirada
          </button>
        </div>

        <SponsorsStrip />

        {alert.visible && <AlertModal alert={alert} />}
        {showExitConfirm && (
          <ExitConfirmModal 
            onCancel={() => setShowExitConfirm(false)} 
            onConfirm={() => { setShowExitConfirm(false); setMode(null); resetTraining(); }} 
          />
        )}
      </div>
    );
  }

  // ========== JUEGO NORMAL (Niveles 1-5, 7) ==========    
  const checkouts = (!input && score <= 170 && score >= 2) ? getCheckoutOptions(score) : null;

  return (
    <div className="js-training">
      {/* Header */}
      <div className="js-game-header">
        <button className="js-back-btn" onClick={() => setShowExitConfirm(true)}>← Salir</button>
        <div className="js-header-logos">
          <img src="/Logo26.png" alt="501 Dart League" className="js-app-logo" />
          <img src="/images/JesusSalate/NuevoLogoSinFondoJesus.png" alt="Jesús Salate" className="js-jesus-logo" />
        </div>
        <div className="js-challenge-info">
          <span className="js-challenge-title">
            {bonusActive ? '🌟 BONUS' : `Desafío ${currentChallengeIndex + 1}/7`}
          </span>
          <span className="js-challenge-desc">{currentChallenge.description}</span>
          <span className="js-challenge-mode">{bonusActive ? `Intento ${bonusAttempts + 1}` : (isEasyMode ? '✨ Principiante' : '🔥 Normal')}</span>
        </div>
      </div>

      {/* Nivel 7: Marcador */}
      {!bonusActive && currentChallengeIndex === 6 && (
        <div className="js-match-score">
          <span>TÚ <strong>{level7Completions}</strong></span>
          <span className="js-match-vs">vs</span>
          <span><strong>{level7RivalScore}</strong> RIVAL</span>
        </div>
      )}

      {/* Progreso */}
      {/* Progreso */}
      {!bonusActive && (
        <div className="js-progress">
          {CHALLENGES.map((_, i) => (
            <div key={i} className={`js-progress-dot ${i < currentChallengeIndex ? 'completed' : i === currentChallengeIndex ? 'active' : ''}`}>
              {i < currentChallengeIndex ? '✓' : i + 1}
            </div>
          ))}
        </div>
      )}

      <div className="js-game-body">
        {/* Panel izquierdo: Rondas */}
        <div className="js-rounds-panel" ref={roundsPanelRef}>
          <h4>Rondas</h4>
          {throwHistory.map((pts, i) => (
            <div key={i} className={`js-round-pill ${i === throwHistory.length - 1 ? 'active' : ''}`}>
              <span className="js-round-index">R{i + 1}</span>
              <span className={`js-round-points ${pts === 'BUST' ? 'bust' : ''}`}>{pts}</span>
            </div>
          ))}
          {throwHistory.length === 0 && <p className="js-rounds-empty">Sin rondas</p>}
        </div>

        {/* Panel central: Score */}
        <div className="js-score-panel">
          {/* 180 Banner */}
          {show180 && (
            <div className="js-180-banner">
              <img src={logo180} alt="180!" className="js-180-img" />
            </div>
          )}
          
          <div className="js-score-display">{score}</div>
          
          {/* Checkouts */}
          <div className="js-checkouts">
            {checkouts && checkouts.length > 0 && checkouts.slice(0, 3).map((co, i) => (
              <div key={i} className={`js-checkout ${i === 0 ? 'primary' : 'secondary'}`}>
                {formatCheckout(co)}
              </div>
            ))}
          </div>

          {/* Stats */}
          <div className="js-stats-row">
            <div className="js-stat">
              <span className="js-stat-label">Rondas</span>
              <span className="js-stat-value">{throwsUsed}/{currentChallenge.maxThrows}</span>
            </div>
            <div className="js-stat">
              <span className="js-stat-label">Average</span>
              <span className="js-stat-value green">{currentAverage.toFixed(2)}</span>
            </div>
            <div className="js-stat">
              <span className="js-stat-label">Intentos</span>
              <span className="js-stat-value">{failedAttempts}</span>
            </div>
          </div>

          {/* Input display */}
          <div className="js-input-display">
            <span className="js-input-value">{input || '—'}</span>
            {moveHistory.length > 0 && (
              <button className="js-undo-btn" onClick={undoLastMove}>↩ Deshacer</button>
            )}
          </div>
        </div>

        {/* Panel derecho: Teclado */}
        <div className="js-numpad">
          {[['1','2','3'], ['4','5','6'], ['7','8','9'], [input.length > 0 ? '←' : 'BUST', '0', 'OK']].map((row, ri) => (
            <div key={ri} className="js-numpad-row">
              {row.map((num) => {
                const isBack = num === '←';
                const isBust = num === 'BUST';
                const isOK = num === 'OK';
                let cls = 'js-numpad-btn';
                if (isBack || isBust) cls += ' danger';
                if (isOK) cls += ' confirm';
                return (
                  <button key={num} className={cls} onClick={() => {
                    if (isBack) handleBackspace();
                    else if (isBust) handleBust();
                    else if (isOK) handleSubmit();
                    else handleNumberPress(num);
                  }}>
                    {num}
                  </button>
                );
              })}
            </div>
          ))}
        </div>
      </div>

      <SponsorsStrip />

      {/* Modals */}
      {alert.visible && <AlertModal alert={alert} />}
      {showDartsModal && (
        <DartsModal 
          closingScore={pendingWinData?.points}
          onSelect={(darts) => { setShowDartsModal(false); processWinWithDarts(darts, pendingWinData); }}
        />
      )}
      {showExitConfirm && (
        <ExitConfirmModal 
          onCancel={() => setShowExitConfirm(false)} 
          onConfirm={() => { 
            setShowExitConfirm(false); 
            if (bonusActive) {
              // Volver a resultados sin perder stats
              setBonusActive(false);
              setShowResults(true);
            } else {
              setMode(null); 
              resetTraining(); 
            }
          }} 
        />
      )}
    </div>
  );
};

// ========== SUB-COMPONENTES ==========

const AlertModal = ({ alert }) => (
  <div className="js-modal-overlay">
    <div className={`js-modal ${alert.type}`}>
      <h2>{alert.title}</h2>
      <p className="js-modal-message">{alert.message}</p>
      <div className="js-modal-buttons">
        {alert.buttons.map((btn, i) => (
          <button key={i} className={`js-btn ${btn.style || 'default'}`} onClick={btn.onClick}>
            {btn.text}
          </button>
        ))}
      </div>
    </div>
  </div>
);

const DartsModal = ({ closingScore, onSelect }) => (
  <div className="js-modal-overlay">
    <div className="js-modal info">
      <h2>🎯 ¡Cierre!</h2>
      <p>¿Con cuántos dardos has cerrado {closingScore}?</p>
      <div className="js-darts-options">
        <button className="js-dart-btn" onClick={() => onSelect(1)}>1 dardo</button>
        <button className="js-dart-btn" onClick={() => onSelect(2)}>2 dardos</button>
        <button className="js-dart-btn" onClick={() => onSelect(3)}>3 dardos</button>
      </div>
    </div>
  </div>
);

const ExitConfirmModal = ({ onCancel, onConfirm }) => (
  <div className="js-modal-overlay">
    <div className="js-modal warning">
      <h2>¿Salir del desafío?</h2>
      <p>Perderás todo el progreso actual.</p>
      <div className="js-modal-buttons">
        <button className="js-btn default" onClick={onCancel}>Cancelar</button>
        <button className="js-btn primary" onClick={onConfirm}>Salir</button>
      </div>
    </div>
  </div>
);

export default JesusSalateTraining;
